import React, { useRef, useLayoutEffect } from "react";
import { Outlet } from "react-router-dom";
import VerticalNav from "components/Navigation/VerticalNav";
import { requireAuth, useAuth } from "utilities/auth";
import {
  AdminCompanyProvider,
  useAdminCompany,
} from "context/AdminCompanyContext";
import AppTopBar from "components/Navigation/AppTopBar";
import { isInternal, isClient } from "utilities/userType";

function ImpersonationBanner() {
  const { activeCompanyId, setActiveCompanyId, childCompanies, isParentCompany } = useAdminCompany();
  const auth = useAuth();
  const ref = useRef(null);

  const isInternalImpersonating =
    isInternal(auth.user) &&
    auth.user.companyId === "sound-admin" &&
    activeCompanyId;

  const isParentSwitched =
    isClient(auth.user) &&
    isParentCompany &&
    activeCompanyId;

  const isImpersonating = isInternalImpersonating || isParentSwitched;

  // Resolve display name for the active company
  const activeCompanyName = isParentSwitched
    ? childCompanies.find((c) => c.companyId === activeCompanyId)?.name || activeCompanyId
    : activeCompanyId;

  // Measure the banner and set a CSS var on #app-shell
  useLayoutEffect(() => {
    const shell = document.getElementById("app-shell");
    if (!shell) return;

    if (!isImpersonating) {
      shell.style.removeProperty("--impersonation-h");
      return;
    }

    const el = ref.current;
    if (!el) return;

    const setVar = () => {
      shell.style.setProperty("--impersonation-h", `${el.offsetHeight}px`);
    };

    setVar();

    // Resize observer to handle responsive changes
    const ro = new ResizeObserver(setVar);
    ro.observe(el);

    // Also handle window resize (safe belt)
    window.addEventListener("resize", setVar);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", setVar);
      shell.style.removeProperty("--impersonation-h");
    };
  }, [isImpersonating]);

  if (!isImpersonating) return null;

  return (
    <div
      ref={ref}
      className="sticky top-0 z-30 w-full bg-brand text-brand-foreground text-sm"
    >
      <div className="container flex items-center justify-center gap-3 py-2">
        <span>
          Viewing as <strong>{activeCompanyName}</strong> — all data reflects this
          company’s environment.
        </span>
        <button
          onClick={() => setActiveCompanyId(null)}
          className="text-brand-foreground btn btn-outline !py-1 !px-2 text-xs"
        >
          {isParentSwitched ? "Back to Portfolio" : "Clear"}
        </button>
      </div>
    </div>
  );
}

function AccountLayout() {
  return (
    <AdminCompanyProvider>
      <div className="min-h-screen bg-background text-foreground">
        <VerticalNav />

        {/* App shell */}
        <div id="app-shell" className="ml-[var(--nav-w,65px)]">
          <ImpersonationBanner />

          {/* Make the top bar sticky below the banner height if present */}
          <div className="sticky top-[var(--impersonation-h,0px)] z-20">
            <AppTopBar />
          </div>

          <main id="content" className="py-6">
            <div className="container space-y-6">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </AdminCompanyProvider>
  );
}

export default requireAuth(AccountLayout);
